import React, { useEffect, useMemo } from 'react';
import PropTypes from "prop-types";
import { useSelector, useDispatch } from 'react-redux';
import {
	reduxForm,
	Field,
	initialize,
	change,
	getFormValues,
	getFormSyncErrors,
	getFormAsyncErrors,
	getFormError
} from 'redux-form';
import {
	Modal,
	Button,
	Icon,
	ProgressIndicator,
	ErrorBanner,
	ReduxControls
} from 'rhombus-react';

import { noop, splitFilename, TextOverflow } from 'utils/common';
import { useTooltipRebuild } from 'hooks/useTooltipRebuild';
import {
	allNamesUnique,
	uniqueNameMultiple,
	asyncValidateMultipleRename,
	baseNameRequiredMultiple
} from "./validators";
import { assetName, richloadName } from 'widgets/forms/validators/Assets';

import "./RenameMultipleModal.css";

const RENAME_MULTIPLE_ASSETS = "rename-multiple-assets";

function formatFilename(value){
	let [base] = splitFilename(value.newName);
	return base;
}

function normalizeFilename(value, prevValue){
	let { extension } = prevValue;

	return {
		...prevValue,
		newName: `${value}${extension}`,
	};
}

export function generateInitialValues(conflicts, suffix){
	let filenames = conflicts.reduce((prev, conflict, index) => {
		let { name } = conflict;
		let [base, extension] = splitFilename(name);

		prev[index] = {
			original: name,
			newName: `${base}${suffix}${extension}`,
			extension,
		};

		return prev;
	}, {});

	return {
		suffix,
		filenames,
		alwaysDirty: false,
	};
}

function NameFields({ filenames = {}, nameValidator }){
	let entries = Object.entries(filenames);

	return entries.map(([key, value]) => {
		let { original, extension } = value || {};
		return (
			<Field
				key = {key}
				name = {`filenames[${key}]`}
				label = {original}
				component = {ReduxControls.Input}
				post = {extension}
				format = {formatFilename}
				normalize = {normalizeFilename}
				validate = {[baseNameRequiredMultiple, uniqueNameMultiple, nameValidator]}
			/>
		);
	});
}

function buildErrorMessages(errors = {}, filenameValues = {}){
	let { filenames = {} } = errors || {};
	let errorMessages = Object.entries(filenames).reduce((prev, [key, value]) => {
		if(!value){
			/*
				If a field had an error at some point, but the user resolved it, `value` will be
				undefined, but there will still be an entry for it, so we return early to skip those.
			*/
			return prev;
		}
		let { original } = filenameValues[key] || {};
		prev.push(
			<div className="name-error" key={key}>
				<div className="original-name"><TextOverflow value={original} /></div>
				<div className="message"> - {value}</div>
			</div>
		);
		return prev;
	}, []);

	return errorMessages;
}

function useErrorBanner(formName, filenameValues = {}){
	let errors = useSelector((state) => {
		return {
			syncErrors: getFormSyncErrors(formName)(state),
			asyncErrors: getFormAsyncErrors(formName)(state),
			formError: getFormError(formName)(state),
		};
	});

	let errorMessages = [];

	if(errors.formError){
		errorMessages.push(errors.formError);
	}
	errorMessages.push(...buildErrorMessages(errors.syncErrors, filenameValues));
	errorMessages.push(...buildErrorMessages(errors.asyncErrors, filenameValues));

	if(!errorMessages.length){
		return null;
	}

	return (
		<ErrorBanner
			messages = {errorMessages}
			title = "Sorry, please fix the errors below and try again."
		/>
	);
}

function RenameMultipleModal(props) {
	const {
		assetType,
		conflicts,

		onBack,
		onMultipleRename,
		handleSubmit,

		asyncValidating,
	} = props;

	const dispatch = useDispatch();
	let {
		suffix,
		filenames,
	} = useSelector(getFormValues(RENAME_MULTIPLE_ASSETS)) || {};
	const nameValidator = useMemo(() => {
		let baseValidator = (assetType === "richloads") ? richloadName : assetName;
		return value => baseValidator(value.newName);
	}, [assetType]);

	function reinitialize(){
		let initialValues = generateInitialValues(conflicts, suffix);
		dispatch(initialize(RENAME_MULTIPLE_ASSETS, initialValues));

		dispatch(change(RENAME_MULTIPLE_ASSETS, "alwaysDirty", true)); /*
			If you have a pristine form, redux-form doesn't bother to async validate on submit,
			so we always set this flag to false on initialize, and then always set it to true on
			reinitialize, to ensure that the form always has a dirty value, and always validates.
		*/
	}

	let $errorBanner = useErrorBanner(RENAME_MULTIPLE_ASSETS, filenames);

	useTooltipRebuild();
	useEffect(reinitialize, []);

	function submitNameWithExtension(values){
		return onMultipleRename(values.filenames);
	}

	function renderContent(){
		return (
			<div className="c-modal-body">
				<div className="main-content">
					<ProgressIndicator isLoading={asyncValidating} />
					<form id={RENAME_MULTIPLE_ASSETS} onSubmit={handleSubmit(submitNameWithExtension)}>
						<div className="fields">
							{$errorBanner}
							<div className="suffix-controls">
								<Field
									name = "suffix"
									className = "suffix-field"
									component = {ReduxControls.Input}
									label = "Append to All Names"
								/>
								<Icon
									className = "reapply-button"
									name = "fas fa-undo"
									data-tip = "Reset and apply appended value to asset names."
									onClick = {reinitialize}
								/>
							</div>
							<hr />
							<div className="name-fields">
								<NameFields
									filenames = {filenames}
									nameValidator = {nameValidator}
								/>
							</div>
						</div>
					</form>
				</div>
			</div>
		);
	}

	function renderFooter(){
		return (
			<>
				<Button onClick={() => onBack()} theme="default">Back</Button>
				<Button
					type = "submit"
					form = {RENAME_MULTIPLE_ASSETS}
					theme = "blue"
					disabled = {!!asyncValidating}
				>
					Rename Assets
				</Button>
			</>
		);
	}

	let modalProps = {
		type: "modal",
		title: `Rename Uploaded Assets`,
		allowFullScreen: false,
		className: "upload-error-modal rename-multiple-modal",
		xClose: false,
		bladeProps: {
			verticallyCenter: true,
		},
		renderContent,
		renderFooter,
	};

	return <Modal {...modalProps} />;
}

RenameMultipleModal.displayName = "RenameMultipleModal";

RenameMultipleModal.propTypes = {
	// Some of these props aren't used in this file, but are necessary for validation
	libraryId: PropTypes.oneOfType([
		PropTypes.string,
		PropTypes.number,
	]),
	assetType: PropTypes.oneOf([
		"videos",
		"images",
		"richloads",
		// "audios",
	]), // Used to check for name collision.  Should be pluralized, e.g. "videos"

	previousNames: PropTypes.arrayOf(PropTypes.string),

	onBack: PropTypes.func,
	onMultipleRename: PropTypes.func,
};

RenameMultipleModal.defaultProps = {
	previousNames: [],

	onBack: noop,
	onSingleRename: noop,
};



export default reduxForm({
	form: RENAME_MULTIPLE_ASSETS,
	initialValues: {
		suffix: "_copy",
		alwaysDirty: false,
	},
	validate: allNamesUnique,
	asyncValidate: asyncValidateMultipleRename,
	asyncBlurFields: [],
})(RenameMultipleModal);
